import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem
} from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy, Refresh, PersonAdd, Edit, Save, Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { authAPI, adminAPI } from '../../services/api';
import { SessionManager } from '../../utils/sessionManager';

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [emailChangeDialogOpen, setEmailChangeDialogOpen] = useState(false);
  const [emailOtp, setEmailOtp] = useState(['', '', '', '']);
  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [emailOtpVerifying, setEmailOtpVerifying] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [showTokenId, setShowTokenId] = useState(false);
  const [showTokenSecret, setShowTokenSecret] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [createAdminOpen, setCreateAdminOpen] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    category: '',
    states: '',
    otp: ['', '', '', '']
  });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const otpInputRefs = [
    useState(null)[0],
    useState(null)[0],
    useState(null)[0],
    useState(null)[0]
  ];

  const indianStates = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry'
  ];

  const userRole = SessionManager.getUserRole();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const [profileResponse, tokensResponse] = await Promise.all([
        authAPI.getProfile(),
        authAPI.getAPITokens()
      ]);

      if (profileResponse.success) {
        const profileData = {
          firstName: profileResponse.data.firstName || '',
          lastName: profileResponse.data.lastName || '',
          email: profileResponse.data.email || '',
          phone: profileResponse.data.phoneNumber || '',
          companyName: profileResponse.data.companyName || '',
          category: profileResponse.data.category || '',
          states: profileResponse.data.states || '',
          membershipNumber: profileResponse.data.membershipNumber || '',
          accountType: profileResponse.data.accountType || 'user',
          tokenId: tokensResponse.success ? (tokensResponse.data.tokenId || 'Not generated yet') : 'Not generated yet',
          tokenSecret: tokensResponse.success ? (tokensResponse.data.tokenSecret || 'Not generated yet') : 'Not generated yet'
        };
        setProfile(profileData);
        setEditForm({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          email: profileData.email,
          phone: profileData.phone,
          companyName: profileData.companyName,
          category: profileData.category,
          states: profileData.states,
          membershipNumber: profileData.membershipNumber
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (editing) {
      // Cancel editing - reset form
      setEditForm({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        phone: profile.phone,
        companyName: profile.companyName,
        category: profile.category,
        states: profile.states,
        membershipNumber: profile.membershipNumber
      });
    }
    setEditing(!editing);
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async () => {
    if (!editForm.firstName || !editForm.lastName) {
      toast.error('First name and last name are required');
      return;
    }

    // Check if email was changed — need OTP verification first
    if (editForm.email && editForm.email !== profile.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editForm.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      setNewEmail(editForm.email);
      setEmailOtpSending(true);
      try {
        const response = await authAPI.sendEmailChangeOTP(editForm.email);
        if (response.success) {
          toast.success('OTP sent to new email for verification');
          setEmailOtpSent(true);
          setEmailChangeDialogOpen(true);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to send OTP');
      } finally {
        setEmailOtpSending(false);
      }
      return; // Don't save other fields yet — wait for email verification
    }

    await saveProfileFields();
  };

  const saveProfileFields = async () => {
    setSaving(true);
    try {
      const response = await authAPI.updateProfile({
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phoneNumber: editForm.phone,
        companyName: editForm.companyName,
        category: editForm.category,
        states: editForm.states,
        membershipNumber: editForm.membershipNumber
      });

      if (response.success) {
        toast.success('Profile updated successfully');
        setProfile(prev => ({
          ...prev,
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          phone: editForm.phone,
          companyName: editForm.companyName,
          category: editForm.category,
          states: editForm.states,
          membershipNumber: editForm.membershipNumber
        }));
        setEditing(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleEmailOTPChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...emailOtp];
    newOtp[index] = value;
    setEmailOtp(newOtp);
    if (value && index < 3) {
      const nextInput = document.querySelector(`input[name="email-otp-${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleEmailOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !emailOtp[index] && index > 0) {
      const prevInput = document.querySelector(`input[name="email-otp-${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyEmailOTP = async () => {
    const otpString = emailOtp.join('');
    if (otpString.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }

    setEmailOtpVerifying(true);
    try {
      const response = await authAPI.verifyEmailChangeOTP(otpString);
      if (response.success) {
        toast.success('Email updated successfully!');
        setProfile(prev => ({ ...prev, email: newEmail }));
        setEditForm(prev => ({ ...prev, email: newEmail }));
        setEmailChangeDialogOpen(false);
        setEmailOtp(['', '', '', '']);
        setEmailOtpSent(false);
        // Now save other profile fields too
        await saveProfileFields();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setEmailOtpVerifying(false);
    }
  };

  const handleResendEmailOTP = async () => {
    setEmailOtpSending(true);
    try {
      await authAPI.sendEmailChangeOTP(newEmail);
      toast.success('OTP resent to new email');
      setEmailOtp(['', '', '', '']);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setEmailOtpSending(false);
    }
  };

  const handleCloseEmailDialog = () => {
    setEmailChangeDialogOpen(false);
    setEmailOtp(['', '', '', '']);
    setEmailOtpSent(false);
  };

  const handleRegenerateToken = async () => {
    setRegenerateDialogOpen(false);
    setRegenerating(true);
    try {
      const response = await authAPI.generateAPITokens();
      if (response.success) {
        setProfile(prev => ({
          ...prev,
          tokenId: response.data.tokenId,
          tokenSecret: response.data.tokenSecret
        }));
        toast.success('API tokens generated successfully!');
      }
    } catch (error) {
      console.error('Error regenerating tokens:', error);
      toast.error('Failed to regenerate tokens: ' + (error.response?.data?.message || error.message));
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (text === 'Not generated yet') {
      toast.warning('Please generate tokens first');
      return;
    }
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleCreateAdminOpen = () => {
    setCreateAdminOpen(true);
  };

  const handleCreateAdminClose = () => {
    setCreateAdminOpen(false);
    setOtpDialogOpen(false);
    setPasswordDialogOpen(false);
    setAdminForm({
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      category: '',
      states: '',
      otp: ['', '', '', '']
    });
    setOtpSent(false);
    setOtpVerified(false);
    setShowPassword(false);
  };

  const handleAdminFormChange = (e) => {
    setAdminForm({
      ...adminForm,
      [e.target.name]: e.target.value
    });
  };

  const handleOTPChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOTP = [...adminForm.otp];
    newOTP[index] = value;
    setAdminForm({ ...adminForm, otp: newOTP });

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.querySelector(`input[name="otp-${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !adminForm.otp[index] && index > 0) {
      const prevInput = document.querySelector(`input[name="otp-${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSendOTP = async () => {
    // Validate required fields
    if (!adminForm.firstName || !adminForm.lastName || !adminForm.email || !adminForm.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(adminForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingOTP(true);
    try {
      const response = await adminAPI.sendAdminCreationOTP(adminForm.email);
      if (response.success) {
        toast.success('OTP sent to email successfully!');
        setOtpSent(true);
        // Close first dialog and open OTP dialog
        setCreateAdminOpen(false);
        setOtpDialogOpen(true);
        // Focus first OTP input
        setTimeout(() => {
          const firstInput = document.querySelector('input[name="otp-0"]');
          if (firstInput) firstInput.focus();
        }, 100);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = adminForm.otp.join('');
    if (otpString.length !== 4) {
      toast.error('Please enter the complete 4-digit OTP');
      return;
    }

    setVerifying(true);
    try {
      const response = await adminAPI.verifyAdminCreationOTP(adminForm.email, otpString);
      if (response.success) {
        setOtpVerified(true);
        toast.success('OTP verified! Please set a password.');
        // Close OTP dialog and open password dialog
        setOtpDialogOpen(false);
        setPasswordDialogOpen(true);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(error.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleCreateAdmin = async () => {
    // Validate form
    if (!adminForm.firstName || !adminForm.lastName || !adminForm.email || !adminForm.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!otpVerified) {
      toast.error('Please verify OTP first');
      return;
    }

    if (!adminForm.password || !adminForm.confirmPassword) {
      toast.error('Please enter password and confirm password');
      return;
    }

    if (adminForm.password !== adminForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate password strength (minimum 6 characters)
    if (adminForm.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    const otpString = adminForm.otp.join('');

    setCreatingAdmin(true);
    try {
      const response = await adminAPI.createAdmin(
        adminForm.firstName,
        adminForm.lastName,
        adminForm.companyName,
        adminForm.email,
        adminForm.password,
        adminForm.phoneNumber,
        adminForm.category,
        adminForm.states,
        null // membershipNumber removed
      );

      if (response.success) {
        toast.success('Admin account created successfully!');
        handleCreateAdminClose();
      }
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error(error.response?.data?.message || 'Failed to create admin account');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleCancelOTP = () => {
    setOtpDialogOpen(false);
    setOtpSent(false);
    setAdminForm(prev => ({ ...prev, otp: ['', '', '', ''] }));
    setCreateAdminOpen(true); // Go back to first dialog
  };

  const handleCancelPassword = () => {
    setPasswordDialogOpen(false);
    setOtpVerified(false);
    setAdminForm(prev => ({ ...prev, password: '', confirmPassword: '' }));
    setOtpDialogOpen(true); // Go back to OTP dialog
  };

  if (loading || !profile) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            Profile
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            {editing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Close />}
                  onClick={handleEditToggle}
                  disabled={saving}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Save />}
                  onClick={handleSaveProfile}
                  disabled={saving}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEditToggle}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Edit Profile
              </Button>
            )}
            {userRole === 'Admin' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAdd />}
                onClick={handleCreateAdminOpen}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Create Admin
              </Button>
            )}
          </Box>
        </Box>

        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={editing ? editForm.firstName : profile.firstName}
                onChange={handleEditChange}
                InputProps={{ readOnly: !editing }}
                InputLabelProps={{ shrink: true }}
                required={editing}
                sx={!editing ? { '& .MuiOutlinedInput-root': { bgcolor: '#f9f9f9' } } : {}}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={editing ? editForm.lastName : profile.lastName}
                onChange={handleEditChange}
                InputProps={{ readOnly: !editing }}
                InputLabelProps={{ shrink: true }}
                required={editing}
                sx={!editing ? { '& .MuiOutlinedInput-root': { bgcolor: '#f9f9f9' } } : {}}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={editing ? editForm.email : profile.email}
                onChange={handleEditChange}
                InputProps={{ readOnly: !editing }}
                InputLabelProps={{ shrink: true }}
                helperText={editing ? "Changing email requires OTP verification" : ""}
                sx={!editing ? { '& .MuiOutlinedInput-root': { bgcolor: '#f9f9f9' } } : {}}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={editing ? editForm.phone : profile.phone}
                onChange={handleEditChange}
                InputProps={{ readOnly: !editing }}
                InputLabelProps={{ shrink: true }}
                sx={!editing ? { '& .MuiOutlinedInput-root': { bgcolor: '#f9f9f9' } } : {}}
              />
            </Grid>

            {profile.accountType !== 'subuser' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="companyName"
                    value={editing ? editForm.companyName : profile.companyName}
                    onChange={handleEditChange}
                    InputProps={{ readOnly: !editing }}
                    InputLabelProps={{ shrink: true }}
                    sx={!editing ? { '& .MuiOutlinedInput-root': { bgcolor: '#f9f9f9' } } : {}}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  {editing ? (
                    <TextField
                      fullWidth
                      select
                      label="Category"
                      name="category"
                      value={editForm.category}
                      onChange={handleEditChange}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Corporate">Corporate</MenuItem>
                      <MenuItem value="CA">CA</MenuItem>
                      <MenuItem value="CMA">CMA</MenuItem>
                      <MenuItem value="CS">CS</MenuItem>
                      <MenuItem value="Tax Practitioner">Tax Practitioner</MenuItem>
                      <MenuItem value="Advocate">Advocate</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      label="Category"
                      value={profile.category}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f9f9f9' } }}
                    />
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  {editing ? (
                    <TextField
                      fullWidth
                      select
                      label="State"
                      name="states"
                      value={editForm.states}
                      onChange={handleEditChange}
                    >
                      <MenuItem value="">Select</MenuItem>
                      {indianStates.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      label="State"
                      value={profile.states || '-'}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f9f9f9' } }}
                    />
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Membership Number"
                    name="membershipNumber"
                    value={editing ? editForm.membershipNumber : (profile.membershipNumber || '')}
                    onChange={handleEditChange}
                    InputProps={{ readOnly: !editing }}
                    InputLabelProps={{ shrink: true }}
                    sx={!editing ? { '& .MuiOutlinedInput-root': { bgcolor: '#f9f9f9' } } : {}}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Box sx={{ mt: 4, pt: 4, borderTop: '2px solid #f0f0f0' }}>
            <Typography variant="h6" gutterBottom>
              API Token Details
            </Typography>
            
            <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
              Use these credentials to authenticate API requests to our verification services. Keep them secure and never share them publicly.
            </Alert>

            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="TokenID"
                  type={showTokenId ? 'text' : 'password'}
                  value={profile.tokenId}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowTokenId(!showTokenId)}
                          edge="end"
                        >
                          {showTokenId ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <IconButton
                          onClick={() => copyToClipboard(profile.tokenId, 'TokenID')}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Token Secret"
                  type={showTokenSecret ? 'text' : 'password'}
                  value={profile.tokenSecret}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowTokenSecret(!showTokenSecret)}
                          edge="end"
                        >
                          {showTokenSecret ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <IconButton
                          onClick={() => copyToClipboard(profile.tokenSecret, 'Token Secret')}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={() => setRegenerateDialogOpen(true)}
                disabled={regenerating}
                size="large"
              >
                {profile.tokenId === 'Not generated yet' ? 'Generate Token' : 'Re Generate Token'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Regenerate Token Confirmation Dialog */}
        <Dialog
          open={regenerateDialogOpen}
          onClose={() => setRegenerateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {profile?.tokenId === 'Not generated yet' ? 'Generate API Tokens' : 'Regenerate API Tokens'}
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mt: 2 }}>
              {profile?.tokenId === 'Not generated yet' 
                ? 'This will generate new API tokens for your account. You can use these tokens to authenticate API requests.'
                : 'Are you sure you want to regenerate your tokens? This will invalidate your current tokens and any applications using them will stop working.'}
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setRegenerateDialogOpen(false)} disabled={regenerating}>
              Cancel
            </Button>
            <Button
              onClick={handleRegenerateToken}
              variant="contained"
              color="primary"
              disabled={regenerating}
            >
              {regenerating ? 'Generating...' : profile?.tokenId === 'Not generated yet' ? 'Generate' : 'Regenerate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Admin Dialog - Step 1: Basic Info */}
        <Dialog 
          open={createAdminOpen} 
          onClose={handleCreateAdminClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create Admin Account</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={adminForm.firstName}
                    onChange={handleAdminFormChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={adminForm.lastName}
                    onChange={handleAdminFormChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="companyName"
                    value={adminForm.companyName}
                    onChange={handleAdminFormChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={adminForm.email}
                    onChange={handleAdminFormChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    value={adminForm.phoneNumber}
                    onChange={handleAdminFormChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Category"
                    name="category"
                    value={adminForm.category}
                    onChange={handleAdminFormChange}
                    required
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Corporate">Corporate</MenuItem>
                    <MenuItem value="CA">CA</MenuItem>
                    <MenuItem value="CMA">CMA</MenuItem>
                    <MenuItem value="CS">CS</MenuItem>
                    <MenuItem value="Tax Practitioner">Tax Practitioner</MenuItem>
                    <MenuItem value="Advocate">Advocate</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="States"
                    name="states"
                    value={adminForm.states}
                    onChange={handleAdminFormChange}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {indianStates.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCreateAdminClose} disabled={sendingOTP}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSendOTP}
              disabled={sendingOTP || !adminForm.email || !adminForm.firstName || !adminForm.lastName || !adminForm.category}
              size="large"
            >
              {sendingOTP ? 'Sending OTP...' : 'Send OTP'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* OTP Verification Dialog - Step 2 */}
        <Dialog 
          open={otpDialogOpen} 
          onClose={handleCreateAdminClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1, fontWeight: 600, color: '#1976d2', fontSize: '1.5rem' }}>
            Verify Your Email
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                We've sent a 4-digit OTP to
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                {adminForm.email}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
              {[0, 1, 2, 3].map((index) => (
                <TextField
                  key={index}
                  name={`otp-${index}`}
                  value={adminForm.otp[index]}
                  onChange={(e) => handleOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleOTPKeyDown(index, e)}
                  inputProps={{
                    maxLength: 1,
                    style: { 
                      textAlign: 'center', 
                      fontSize: '32px', 
                      fontWeight: 600,
                      padding: '16px'
                    }
                  }}
                  sx={{
                    width: '64px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderWidth: 2,
                        borderColor: adminForm.otp[index] ? '#1976d2' : '#ddd'
                      }
                    }
                  }}
                  autoFocus={index === 0}
                />
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
              OTP expires in 10 minutes
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 3, pt: 0 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyOTP}
              disabled={adminForm.otp.join('').length !== 4 || verifying}
            >
              {verifying ? 'Verifying...' : 'Verify OTP'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancelOTP}
              disabled={verifying}
            >
              Back
            </Button>
          </DialogActions>
        </Dialog>

        {/* Password Dialog - Step 3 */}
        <Dialog 
          open={passwordDialogOpen} 
          onClose={handleCreateAdminClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Set Admin Password</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                Email verified successfully! Please set a password for the admin account.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={adminForm.password}
                    onChange={handleAdminFormChange}
                    required
                    helperText="Minimum 6 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={adminForm.confirmPassword}
                    onChange={handleAdminFormChange}
                    required
                    error={adminForm.confirmPassword && adminForm.password !== adminForm.confirmPassword}
                    helperText={adminForm.confirmPassword && adminForm.password !== adminForm.confirmPassword ? 'Passwords do not match' : ''}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCancelPassword} disabled={creatingAdmin}>
              Back
            </Button>
            <Button 
              onClick={handleCreateAdmin} 
              variant="contained" 
              disabled={creatingAdmin || !adminForm.password || !adminForm.confirmPassword}
            >
              {creatingAdmin ? 'Creating...' : 'Create Admin'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email Change OTP Verification Dialog */}
        <Dialog
          open={emailChangeDialogOpen}
          onClose={handleCloseEmailDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1, fontWeight: 600 }}>
            Verify New Email
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                We've sent a 4-digit OTP to
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                {newEmail}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
              {[0, 1, 2, 3].map((index) => (
                <TextField
                  key={index}
                  name={`email-otp-${index}`}
                  value={emailOtp[index]}
                  onChange={(e) => handleEmailOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleEmailOTPKeyDown(index, e)}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: 'center',
                      fontSize: '28px',
                      fontWeight: 600,
                      padding: '12px'
                    }
                  }}
                  sx={{
                    width: '56px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderWidth: 2,
                        borderColor: emailOtp[index] ? '#1976d2' : '#ddd'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2'
                      }
                    }
                  }}
                  autoFocus={index === 0}
                />
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
              Didn't receive the code?{' '}
              <Button
                size="small"
                onClick={handleResendEmailOTP}
                disabled={emailOtpSending}
                sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0, p: 0 }}
              >
                {emailOtpSending ? 'Sending...' : 'Resend OTP'}
              </Button>
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 3, pt: 1 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyEmailOTP}
              disabled={emailOtp.join('').length !== 4 || emailOtpVerifying}
            >
              {emailOtpVerifying ? 'Verifying...' : 'Verify & Update Email'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCloseEmailDialog}
              disabled={emailOtpVerifying}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default Profile;
